// src/client/pages/OrderTracker.jsx
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../core/api'; // ✅ utilise l'axios configuré

const statusSteps = [
  'en attente',
  'en préparation',
  'prête',
  'en livraison',
  'livrée',
  'retirée'
];

const statusMessages = {
  'en attente': '⏳ Votre commande est enregistrée, on s’y met très vite !',
  'en préparation': '👨‍🍳 Nos chefs préparent votre festin avec amour…',
  'prête': '🔔 C’est prêt ! Il ne manque plus qu’un dernier check !',
  'en livraison': '🛵 Votre commande est en route ! Préparez vos papilles !',
  'livrée': '🍽️ Votre commande a été livrée ! Bon appétit 😋',
  'retirée': '✅ Vous avez récupéré votre commande ! Merci de votre visite 🙌',
};

const getStatusIconPath = (status) => {
  const name = status
    .replace(/ /g, '-')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return `/icons/${name}.svg`;
};

const OrderTracker = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const isMongoId = orderId.length === 24;
        const endpoint = isMongoId
          ? `/api/orders/track/id/${orderId}`
          : `/api/orders/track/${orderId}`;

        // ✅ on passe par api.js → ajoute automatiquement x-client-key
        const res = await api.get(endpoint);
        setOrder(res.data);
      } catch (err) {
        console.error('❌ Erreur OrderTracker :', err);
        setError(err.response?.data?.message || 'Commande non trouvée');
      }
    };

    fetchOrder();
    const interval = setInterval(fetchOrder, 20000);
    return () => clearInterval(interval);
  }, [orderId]);

  if (error) return <p className="text-danger">❌ {error}</p>;
  if (!order) return <p>Chargement en cours...</p>;

  const currentStepIndex = statusSteps.indexOf(order.status);
  const isFinished =
    (order.orderType === 'livraison' && order.status === 'livrée') ||
    (order.orderType === 'retrait' && order.status === 'retirée');

  const currentMessage = statusMessages[order.status];

  return (
    <div className="container py-5 theme6-order-tracker">
      <button className="btn btn-outline-secondary mb-3" onClick={() => navigate(-1)}>
        ← Retour
      </button>

      <h2 className="mb-4">Suivi de commande</h2>
      <p><strong>Nom :</strong> {order.name}</p>
      <p><strong>Téléphone :</strong> {order.phone}</p>
      <p><strong>Adresse :</strong> {order.address}</p>
      <p><strong>Type :</strong> {order.orderType}</p>
      <p><strong>Total :</strong> {order.total.toFixed(2)} €</p>

      <div className="theme6-timeline-wrapper">
        <div className="theme6-timeline">
          {statusSteps.map((step, i) => {
            const isPast = i < currentStepIndex;
            const isActive = i === currentStepIndex;

            return (
              <div
                key={step}
                className={`theme6-step ${isPast ? 'past' : ''} ${isActive ? 'active' : ''}`}
              >
                <div className="theme6-icon">
                  <img src={getStatusIconPath(step)} alt={step} className="status-icon-svg" />
                </div>
                <div className="theme6-label">{step}</div>
              </div>
            );
          })}
        </div>
      </div>

      <div key={order.status} className="theme6-status-message animated-status">
        <img
          src={getStatusIconPath(order.status)}
          alt="icon"
          className="status-icon-svg me-2"
        />
        <span className="message">{currentMessage}</span>
      </div>

      {isFinished && (
        <div className="alert alert-success mt-4">
          🎉 Merci pour votre commande ! À très bientôt.
        </div>
      )}

      <p className="text-muted">
        Dernière mise à jour : {new Date(order.updatedAt).toLocaleString()}
      </p>
    </div>
  );
};

export default OrderTracker;
